/**
 * chapterSequence – the order a chapter's topics are taught in.
 *
 * The teacher picks a topic from /lessons, then moves the class on from inside
 * the player. Both have to agree on what "next" means, or the player skips a
 * topic the list shows, or offers one the list hides. So this mirrors the rules
 * in Lessons.jsx: only approved topics, ordered by topic_priority.
 *
 * A chapter is usually stored as several curriculum_chapters documents sharing
 * curriculum, class, subject and chapter_number — most often one topic per
 * document, sometimes one copy per language. Lessons.jsx groups them; so does
 * this, which is why it takes every document of the chapter and reports which
 * document each topic lives in: that is the chapter_id its lesson loads from.
 *
 * Takes raw documents, because the chapter normalizer drops the approval fields
 * this needs.
 */

export interface SequencedTopic {
  id: string;
  name: string;
  priority: number;
  /** The curriculum_chapters document holding this topic. */
  chapterId: string;
}

export interface ChapterDoc {
  id: string;
  data: any;
}

function isTopicVisible(topic: any, chapterApproved: boolean): boolean {
  const approval = topic?.approval;
  const approved =
    approval?.approved === true || approval?.approved === 'true' || topic?.approved === true;
  const hasApproval =
    !!approval && typeof approval === 'object' && Object.keys(approval).length > 0;
  return approved || (chapterApproved && !hasApproval);
}

export function sequenceTopics(docs: ChapterDoc[]): SequencedTopic[] {
  const seen = new Set<string>();
  const out: SequencedTopic[] = [];
  for (const { id, data } of docs) {
    const topics: any[] = Array.isArray(data?.topics) ? data.topics : [];
    const chapterApproved = data?.approved === true;
    for (const t of topics) {
      const topicId = t?.topic_id ? String(t.topic_id) : '';
      // A topic copied into each language's document is still one topic.
      if (!topicId || seen.has(topicId) || !isTopicVisible(t, chapterApproved)) continue;
      seen.add(topicId);
      out.push({
        id: topicId,
        name: String(t.topic_name || 'Untitled topic'),
        priority: Number(t.topic_priority) || 1,
        chapterId: id,
      });
    }
  }
  // Array.prototype.sort is stable, so equal priorities keep document order.
  return out.sort((a, b) => a.priority - b.priority);
}

/** The fields that make documents one chapter, or null if the document lacks one. */
export function chapterGroupOf(data: any): { curriculum: unknown; class: unknown; subject: unknown; chapter_number: unknown } | null {
  const group = { curriculum: data?.curriculum, class: data?.class, subject: data?.subject, chapter_number: data?.chapter_number };
  return Object.values(group).every((v) => v !== undefined && v !== null && v !== '') ? group : null;
}
